/* eslint-disable no-unused-vars */
import InfoIcon from "@mui/icons-material/Info";
import {
	Box,
	FormControlLabel,
	Grid,
	Radio,
	RadioGroup,
	Skeleton,
	Switch,
	Tooltip,
} from "@mui/material";
import { useEffect, useMemo } from "react";
import { Link } from "react-router";

import TooltipContainer from "../TooltipContainer";
import ControlledCheckbox from "../controlled/ControlledCheckbox";
import ControlledRadioGroup from "../controlled/ControlledRadioGroup";
import ControlledSelect from "../controlled/ControlledSelect";

import { Controller, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../features/auth/AuthContext";
import { useGetIntegrationStatusQuery } from "../../features/integrations/integrationsApiSlice";
import { useGetCurrentUserMailingGroupsQuery } from "../../features/recipients/recipientsApiSlice";
import { CargoFormSms } from "../../features/sms/cargo-form";
import { DO_NOT_USE_TIMOCOM_FOR_COMPANY_IDS } from "../../utils/companion";

export const SalesPart = ({
	control,
	errors,
	settings,
	resetField,
	setValue,
	getValues,
	watch,
}) => {
	const { t } = useTranslation();
	const { user } = useAuth();
	const hasCustomTransConnection = user?.hasCustomTransConnection;
	const userCompanyId = user?.companyId;

	const {
		data: mailingGroups,
		error: mailingGroupsError,
		isLoading: isLoadingGroups,
	} = useGetCurrentUserMailingGroupsQuery();

	const {
		data: integrationsStatuses,
		error: integrationsError,
		isLoading: isLoadingIntegrations,
	} = useGetIntegrationStatusQuery();

	const mailingGroupOptions = useMemo(() => {
		if (!mailingGroups?.groups) {
			return [];
		}

		return Object.entries(mailingGroups?.groups).map(([key, name]) => ({
			value: key,
			label: name,
		}));
	}, [mailingGroups?.groups]);

	const handleUncheckAllStocks = () => {
		const isChecked = getValues("shouldPostOnStock");
		const transEnabled = hasCustomTransConnection
			? Boolean(integrationsStatuses?.transEuStatus)
			: true;
		const timocomEnabled =
			!DO_NOT_USE_TIMOCOM_FOR_COMPANY_IDS.has(userCompanyId);
		const telerouteEnabled = Boolean(integrationsStatuses?.telerouteStatus);
		const anyEnabled = transEnabled || timocomEnabled || telerouteEnabled;

		if (isChecked) {
			setValue("postOnTrans", transEnabled);
			setValue("postOnTimocom", timocomEnabled);
			setValue("postOnTeleroute", telerouteEnabled);
			setValue("shouldPostAfter", anyEnabled ? "0" : null);
			setValue("gatherPriceProposals", timocomEnabled);
			setValue("shouldRefreshOffer", anyEnabled);
		} else {
			setValue("postOnTrans", false);
			setValue("postOnTimocom", false);
			setValue("postOnTeleroute", false);
			setValue("shouldPostAfter", null);
			setValue("gatherPriceProposals", false);
			setValue("shouldRefreshOffer", false);
		}
	};

	const watchCargoTypeId = watch("cargoTypeId");
	const isCheckedPostOnTrans = useWatch({
		control,
		name: "postOnTrans",
	});
	const defaultPostOnTrans = hasCustomTransConnection
		? Boolean(integrationsStatuses?.transEuStatus)
		: true;
	const effectivePostOnTrans = isCheckedPostOnTrans ?? defaultPostOnTrans;

	const showAdditionalField =
		hasCustomTransConnection &&
		integrationsStatuses?.transEuStatus &&
		Boolean(effectivePostOnTrans);

	useEffect(() => {
		if (!hasCustomTransConnection && !integrationsStatuses?.transEuStatus) {
			return;
		}

		if (showAdditionalField && !watchCargoTypeId) {
			setValue("cargoTypeId", "PALETTE", { shouldValidate: true });
		}

		if (!showAdditionalField && watchCargoTypeId) {
			setValue("cargoTypeId", null, { shouldValidate: true });
		}
	}, [
		integrationsStatuses?.transEuStatus,
		hasCustomTransConnection,
		showAdditionalField,
		watchCargoTypeId,
		setValue,
	]);

	const isCheckedPostOnTimocom = useWatch({
		control,
		name: "postOnTimocom",
		defaultValue: getValues("postOnTimocom") ?? true,
	});
	const showTimocomAdditionalField = Boolean(isCheckedPostOnTimocom);

	const cargosValue = getValues("cargos");

	return (
        <Grid container rowSpacing={1.5} columnSpacing={1} sx={{ py: 1, px: 2 }}>
            <Grid
				container
				rowSpacing={0.5}
				columnSpacing={1}
				size={{
					lg: 12,
				}}
			>
				<Grid
					size={{
						lg: 12,
					}}
				>
					<Box sx={{ mt: "9px" }}>
						<ControlledCheckbox
							control={control}
							margin="normal"
							name="mailingToGroups"
							label={
								<span>
									{t("mailing_to_trusted_and_gathering_offers")}{" "}
									<Link
										style={{
											marginLeft: "20px",
											color: "#0070C0",
										}}
										to="/recipients"
									>
										{t("manage_groups")}
									</Link>
								</span>
							}
						/>
					</Box>
				</Grid>
				<Grid
					container
					rowSpacing={0}
					columnSpacing={1}
					sx={{ ml: "20px" }}
					size={{
						lg: 11,
					}}
				>
					<Grid
						size={{
							lg: 4,
						}}
					>
						<Box sx={{ alignItems: "center", display: "flex" }}>
							{isLoadingGroups ? (
								<Skeleton variant="rectangular" width={155.77} height={36.72} />
							) : (
								<ControlledSelect
									control={control}
									allowRefresh={true}
									resetField={resetField}
									onChange={() =>
										setValue("mailingToGroups", true, {
											shouldValidate: true,
										})
									}
									margin="none"
									name="mailingGroups_1"
									label={t("group_select", { number: 1 })}
									defaultValue=""
									options={mailingGroupOptions}
								/>
							)}
						</Box>
					</Grid>
					<Grid
						size={{
							lg: 4,
						}}
					>
						<Box sx={{ alignItems: "center", display: "flex" }}>
							{isLoadingGroups ? (
								<Skeleton variant="rectangular" width={155.77} height={36.72} />
							) : (
								<ControlledSelect
									control={control}
									allowRefresh={true}
									resetField={resetField}
									onChange={() =>
										setValue("mailingToGroups", true, {
											shouldValidate: true,
										})
									}
									margin="none"
									name="mailingGroups_2"
									label={t("group_select", { number: 2 })}
									defaultValue=""
									options={mailingGroupOptions}
								/>
							)}
						</Box>
					</Grid>
					<Grid
						size={{
							lg: 4,
						}}
					>
						<Box sx={{ alignItems: "center", display: "flex" }}>
							{isLoadingGroups ? (
								<Skeleton variant="rectangular" width={155.77} height={36.72} />
							) : (
								<ControlledSelect
									control={control}
									allowRefresh={true}
									resetField={resetField}
									onChange={() =>
										setValue("mailingToGroups", true, {
											shouldValidate: true,
										})
									}
									margin="none"
									name="mailingGroups_3"
									label={t("group_select", { number: 3 })}
									defaultValue=""
									options={mailingGroupOptions}
								/>
							)}
						</Box>
					</Grid>
				</Grid>
			</Grid>
            {isLoadingIntegrations ? (
				<Grid
					rowSpacing={0.5}
					columnSpacing={1}
					size={{
						lg: 12,
					}}
				>
					<Skeleton variant="rectangular" height={122.5} />
				</Grid>
			) : (
				<>
					<Grid
						container
						rowSpacing={0.5}
						columnSpacing={1}
						size={{
							lg: 12,
						}}
					>
						<Grid
							size={{
								lg: 12,
							}}
						>
							<Box sx={{ display: "flex" }}>
								<ControlledCheckbox
									control={control}
									margin="normal"
									name="shouldPostOnStock"
									label={<span>{t("publish_on_stocks")}</span>}
									defaultValue={
										integrationsStatuses.transEuStatus ||
										integrationsStatuses.timocomStatus ||
										integrationsStatuses.telerouteStatus ||
										integrationsStatuses.transporeonStatus ||
										integrationsStatuses.comarrStatus ||
										true
									}
									disabled={
										!integrationsStatuses.transEuStatus &&
										!integrationsStatuses.timocomStatus &&
										!integrationsStatuses.telerouteStatus &&
										!integrationsStatuses.transporeonStatus &&
										!integrationsStatuses.comarrStatus &&
										false
									}
									onChange={handleUncheckAllStocks}
								/>
							</Box>
						</Grid>
						<Grid size={{ lg: 12 }}>
							<Box sx={{ display: "flex", ml: "28px" }}>
								<ControlledCheckbox
									control={control}
									margin="normal"
									name="postOnTrans"
									label={<span>Trans</span>}
									defaultValue={
										hasCustomTransConnection
											? integrationsStatuses.transEuStatus
											: true
									}
									disabled={
										hasCustomTransConnection
											? !integrationsStatuses.transEuStatus
											: false
									}
									onChange={() =>
										setValue("shouldPostOnStock", true, {
											shouldValidate: true,
										})
									}
									error={!!errors?.postOnTrans}
								/>
								<ControlledCheckbox
									control={control}
									margin="normal"
									name="postOnTimocom"
									label={<span>TC</span>}
									defaultValue={
										!DO_NOT_USE_TIMOCOM_FOR_COMPANY_IDS.has(userCompanyId)
									}
									disabled={
										!!DO_NOT_USE_TIMOCOM_FOR_COMPANY_IDS.has(userCompanyId)
									}
									onChange={() =>
										setValue("shouldPostOnStock", true, {
											shouldValidate: true,
										})
									}
									error={!!errors?.postOnTimocom}
								/>
								<ControlledCheckbox
									control={control}
									margin="normal"
									name="postOnTeleroute"
									label={<span>Teleroute</span>}
									defaultValue={integrationsStatuses.telerouteStatus}
									disabled={!integrationsStatuses.telerouteStatus}
									onChange={() =>
										setValue("shouldPostOnStock", true, {
											shouldValidate: true,
										})
									}
									error={!!errors?.postOnTeleroute}
								/>
								<ControlledCheckbox
									control={control}
									margin="normal"
									name="postOnTransporeon"
									label={<span>Transporeon</span>}
									defaultValue={integrationsStatuses.transporeonStatus}
									disabled={!integrationsStatuses.transporeonStatus}
									onChange={() =>
										setValue("shouldPostOnStock", true, {
											shouldValidate: true,
										})
									}
									error={!!errors?.postOnTransporeon}
								/>
								<ControlledCheckbox
									control={control}
									margin="normal"
									name="postOnComarr"
									label={<span>ComArr</span>}
									defaultValue={integrationsStatuses.comarrStatus}
									disabled={!integrationsStatuses.comarrStatus}
									onChange={() =>
										setValue("shouldPostOnStock", true, {
											shouldValidate: true,
										})
									}
									error={!!errors?.postOnComarr}
								/>
							</Box>
						</Grid>
						<Grid
							sx={{ ml: "28px", mt: "6px" }}
							size={{
								lg: 12,
							}}
						>
							<Box
								sx={{
									display: "flex",
									gap: "20px",
								}}
							>
								<Box sx={{ flex: "0 0 auto" }}>
									<ControlledRadioGroup
										className="radio-post-after"
										control={control}
										margin="normal"
										name="shouldPostAfter"
										label={t("publish_when")}
										defaultValue={
											// eslint-disable-next-line no-constant-condition
											integrationsStatuses.transEuStatus ||
											integrationsStatuses.timocomStatus ||
											integrationsStatuses.telerouteStatus ||
											integrationsStatuses.transporeonStatus ||
											integrationsStatuses.comarrStatus ||
											true
												? "0"
												: null
										}
										disabled={
											!integrationsStatuses.transEuStatus &&
											!integrationsStatuses.timocomStatus &&
											!integrationsStatuses.telerouteStatus &&
											!integrationsStatuses.transporeonStatus &&
											!integrationsStatuses.comarrStatus &&
											false
										}
										options={[
											{ value: 0, label: t("right_away") },
											{ value: 10, label: t("after_10_min") },
											{ value: 20, label: t("after_20_min") },
										]}
									/>
									<Box
										sx={{
											mt: "10px",
											display: "flex",
											alignItems: "center",
										}}
									>
										<Controller
											control={control}
											name="shouldRefreshOffer"
											defaultValue={
												integrationsStatuses.transEuStatus ||
												integrationsStatuses.timocomStatus ||
												integrationsStatuses.telerouteStatus
											}
											render={({ field }) => {
												return (
													<Box sx={{ position: "relative" }}>
														<RadioGroup
															className="radio-post-after"
															sx={{
																gap: "8px !important",
															}}
															{...field}
															value={
																field.value === true
																	? "true"
																	: field.value === false
																		? "false"
																		: "notExisting"
															}
															onChange={(e) =>
																field.onChange(e.target.value === "true")
															}
														>
															<Box
																sx={{
																	display: "flex",
																	alignItems: "center",
																	gap: "4px",
																}}
															>
																<FormControlLabel
																	value="true"
																	control={<Radio />}
																	label={
																		<span style={{ whiteSpace: "nowrap" }}>
																			{t("refresh_offer_after", { value: 35 })}
																		</span>
																	}
																	disabled={
																		!integrationsStatuses.transEuStatus &&
																		!integrationsStatuses.timocomStatus &&
																		!integrationsStatuses.telerouteStatus
																	}
																/>
																<Tooltip
																	title={
																		<TooltipContainer>
																			<p style={{ margin: 0 }}>
																				{t(
																					"refreshing_offer_will_be_active_until",
																				)}
																			</p>
																		</TooltipContainer>
																	}
																	placement="top-start"
																	slotProps={{
																		popper: {
																			sx: {
																				"& > div": {
																					background: "#fff",
																					p: 0,
																					fontSize: "11px",
																				},
																			},
																		},
																	}}
																>
																	<InfoIcon sx={{ color: "#F07C00" }} />
																</Tooltip>
															</Box>
															<Box>
																<FormControlLabel
																	sx={{ width: "149.7px" }}
																	value="false"
																	control={<Radio />}
																	label={t("refresh_manually")}
																	disabled={
																		!integrationsStatuses.transEuStatus &&
																		!integrationsStatuses.timocomStatus &&
																		!integrationsStatuses.telerouteStatus
																	}
																/>
															</Box>
														</RadioGroup>
													</Box>
												);
											}}
										/>
									</Box>
								</Box>
								<Box
									sx={{
										flex: "1 1 100%",
										display: "flex",
										flexDirection: "column",
										alignItems: "flex-end",
									}}
								>
									{showAdditionalField && (
										<Box sx={{ flex: "1 1 100%", minWidth: "130px" }}>
											<ControlledSelect
												control={control}
												margin="none"
												name="cargoTypeId"
												label={t("cargo_type_label") + " *"}
												defaultValue=""
												error={!!errors?.cargoTypeId}
												onChange={() => {}}
												options={Object.entries(
													settings.vehicleOptions.cargoTypesOptions,
												).map(([value, label]) => {
													return { value, label };
												})}
												sx={{ maxWidth: "130px" }}
											/>
										</Box>
									)}
									{showTimocomAdditionalField && (
										<Box
											sx={{
												display: "flex",
												flexDirection: "column",
												alignItems: "flex-end",
											}}
										>
											<Controller
												control={control}
												name="companionUseTimocomBlacklist"
												defaultValue={
													user.preferences?.timocom?.shouldUseBlacklist || false
												}
												render={({ field }) => {
													return (
														<FormControlLabel
															sx={{ mr: 0 }}
															control={
																<Switch
																	sx={{
																		".Mui-checked": {
																			color: "#63c29a",
																		},
																		".Mui-checked + .MuiSwitch-track": {
																			backgroundColor: "#63c29a",
																		},
																	}}
																	checked={field.value}
																	onChange={field.onChange}
																	slotProps={{
																		input: { "aria-label": "controlled" },
																	}}
																/>
															}
															label={t("companion_use_timocom_blacklist")}
														/>
													);
												}}
											/>
											<Controller
												control={control}
												name="companionUseTimocomMessenger"
												defaultValue={
													user.preferences?.timocom?.shouldUseMessenger || false
												}
												render={({ field }) => {
													return (
														<FormControlLabel
															sx={{ mr: 0 }}
															control={
																<Switch
																	sx={{
																		".Mui-checked": {
																			color: "#63c29a",
																		},
																		".Mui-checked + .MuiSwitch-track": {
																			backgroundColor: "#63c29a",
																		},
																	}}
																	checked={field.value}
																	onChange={field.onChange}
																	slotProps={{
																		input: { "aria-label": "controlled" },
																	}}
																/>
															}
															label={t("companion_use_timocom_messenger")}
														/>
													);
												}}
											/>
											<Controller
												control={control}
												name="companionUseTimocomQuickAdd"
												defaultValue={
													!!cargosValue?.some(
														(c) => c.companionUseTimocomQuickAdd,
													) || false
												}
												render={({ field }) => {
													return (
														<FormControlLabel
															sx={{ mr: 0 }}
															control={
																<Switch
																	sx={{
																		".Mui-checked": {
																			color: "#63c29a",
																		},
																		".Mui-checked + .MuiSwitch-track": {
																			backgroundColor: "#63c29a",
																		},
																	}}
																	checked={
																		cargosValue?.some(
																			(c) => c.companionUseTimocomQuickAdd,
																		)
																			? true
																			: field.value
																	}
																	disabled={
																		!!cargosValue?.some(
																			(c) => c.companionUseTimocomQuickAdd,
																		) || false
																	}
																	onChange={field.onChange}
																	slotProps={{
																		input: { "aria-label": "controlled" },
																	}}
																/>
															}
															label={
																<Box
																	sx={{
																		display: "flex",
																		alignItems: "center",
																		gap: 0.5,
																	}}
																>
																	<span>
																		{t("companion_use_timocom_quick_add")}
																	</span>
																	<Tooltip
																		title={
																			<TooltipContainer>
																				<p style={{ margin: 0 }}>
																					{t(
																						"companion_use_timocom_quick_add_info",
																					)}
																				</p>
																			</TooltipContainer>
																		}
																		placement="top-start"
																		slotProps={{
																			popper: {
																				sx: {
																					"& > div": {
																						background: "#fff",
																						p: 0,
																						fontSize: "11px",
																					},
																				},
																			},
																		}}
																	>
																		<InfoIcon sx={{ color: "#F07C00" }} />
																	</Tooltip>
																</Box>
															}
														/>
													);
												}}
											/>
										</Box>
									)}
								</Box>
							</Box>
						</Grid>
					</Grid>
					<Grid
						item
						size={{
							lg: 12,
						}}
					>
						<Box
							sx={{
								display: "flex",
								alignItems: "center",
								justifyContent: "space-between",
							}}
						>
							<Box
								sx={{
									display: "flex",
									alignItems: "center",
									justifyContent: "flex-start",
								}}
							>
								<ControlledCheckbox
									control={control}
									margin="normal"
									error={!!errors?.shouldBePublic}
									name="shouldBePublic"
									label={<span>{t("whatsapp_add_to_table")}</span>}
								/>
								<Tooltip
									title={
										<TooltipContainer>
											<p style={{ margin: 0 }}>{t("whatsapp_info_tooltip")}</p>
										</TooltipContainer>
									}
									placement="top-start"
									slotProps={{
                                        popper: {
                                            sx: {
                                                "& > div": {
                                                    background: "#fff",
                                                    p: 0,
                                                    fontSize: "11px",
                                                },
                                            },
                                        }
                                    }}
								>
									<InfoIcon sx={{ color: "#F07C00" }} />
								</Tooltip>
							</Box>
						</Box>
					</Grid>
					<Grid
						item
						size={{
							lg: 12,
						}}
					>
						<Box sx={{ display: "flex", alignItems: "center", gap: "20px" }}>
							<Box sx={{ display: "flex", alignItems: "center" }}>
								<ControlledCheckbox
									control={control}
									margin="normal"
									error={!!errors?.sendSMS}
									name="sendSMS"
									label={<span>Wyślij SMS</span>}
								/>
								<Tooltip
									PopperProps={{
										sx: {
											"& > div": {
												background: "#fff",
												p: 0,
												fontSize: "11px",
											},
										},
									}}
									title={
										<TooltipContainer>
											<p style={{ margin: 0 }}>Opis tutaj</p>
										</TooltipContainer>
									}
									placement="top-start"
								>
									<InfoIcon sx={{ color: "#F07C00" }} />
								</Tooltip>
							</Box>
							<CargoFormSms control={control} />
						</Box>
					</Grid>
				</>
			)}
        </Grid>
    );
};
